import { useEffect, useRef, useState } from 'react';
import { fetchGifs } from '@/lib/gifs/gif-client';
import { gifCache } from '@/components/gif-picker/gif-picker';
import type { GifResult, GifMode } from '@/components/gif-picker/types';
import { useLaunchAnimation } from '@/components/popover-launch/use-launch-animation';
import styles from './gif-preview-section.module.css';

const PREVIEW_LIMIT = 6;

function cacheKey(mode: GifMode, query: string) {
  return `${mode}:${query}:${PREVIEW_LIMIT}`;
}

interface GifPreviewSectionProps {
  mode: GifMode;
  onSelect: (result: GifResult) => void;
  /** Search query — empty string uses trending */
  query?: string;
  /** Delay before firing the fetch (ms) — stagger concurrent sections to avoid rate limits */
  fetchDelay?: number;
}

/**
 * Lightweight preview strip for the "All" tab.
 * - Reads from the shared gifCache immediately (no loading flash on re-mount).
 * - Shows static placeholder tiles while loading — no animated skeleton.
 * - Renders up to 6 images once loaded; no infinite scroll.
 * - On error, stays on placeholders gracefully.
 */
export function GifPreviewSection({ mode, onSelect, query = '', fetchDelay = 0 }: GifPreviewSectionProps) {
  const key = cacheKey(mode, query);
  const cached = gifCache.get(key);

  const [results, setResults] = useState<GifResult[]>(cached?.results.slice(0, PREVIEW_LIMIT) ?? []);
  const [failed, setFailed] = useState(false);
  const mountedRef = useRef(true);
  const { launchingId, launch } = useLaunchAnimation();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    // Already have results from cache — skip fetch
    const hit = gifCache.get(key);
    if (hit && hit.results.length > 0) {
      setResults(hit.results.slice(0, PREVIEW_LIMIT));
      return;
    }

    const timer = setTimeout(() => {
      fetchGifs(mode, query, PREVIEW_LIMIT, 0)
        .then(({ results: fetched, totalCount }) => {
          if (!mountedRef.current) return;
          const slice = fetched.slice(0, PREVIEW_LIMIT);
          gifCache.set(key, {
            results: slice,
            totalCount,
            offset: slice.length,
          });
          setResults(slice);
        })
        .catch(() => {
          if (!mountedRef.current) return;
          setFailed(true);
        });
    }, fetchDelay);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fetchDelay]);

  // Placeholders shown while loading or on error
  if (results.length === 0) {
    return (
      <div className={styles.stickerGrid} data-failed={failed || undefined}>
        {Array.from({ length: PREVIEW_LIMIT }).map((_, i) => (
          <div key={i} className={styles.stickerPlaceholder} data-testid="gif-preview-placeholder" />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.stickerGrid}>
      {results.map(result => (
        <button
          key={result.id}
          type="button"
          className={styles.stickerItem}
          aria-label={result.title}
          data-launching={launchingId === result.id || undefined}
          onClick={() => launch(result.id, () => onSelect(result))}
        >
          <img
            src={mode === 'stickers' ? (result.webpUrl ?? result.stillUrl) : result.stillUrl}
            alt={result.title}
            className={mode === 'stickers' ? styles.stickerImg : styles.gifImg}
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}
